import React, { useState, useEffect } from 'react';
import FightingArts from '../All Cards/FightingArts';
import SecretFightingArts from '../All Cards/SecretFightingArts';
import StrainFightingArts from '../All Cards/StrainFightingArts';
import Philosophy from '../All Cards/Philosophy';
import Knowledge from '../All Cards/Knowledge';

import { usePersistedState } from "./SheetState";


const safeName = (v) => (typeof v === 'string' ? v : '');

const ArcCharacterSheets = ({ showFightingArts, showPhilosophy, showKnowledge, showHuntXP, storageKey } = {}) => {
  const keyBase = storageKey || 'kdm_arcCharacterSheets__single';

  const categories = {
    "Fighting Arts": FightingArts,
    "Strain Fighting Arts": StrainFightingArts,
    "Secret Fighting Arts": SecretFightingArts,
  };

  const [selectedArts, setSelectedArts] = usePersistedState(`${keyBase}__selectedArts`, [null, null]);
  const [selectedCategories, setSelectedCategories] = usePersistedState(`${keyBase}__selectedCategories`, ['', '']);
  const [observationStates, setObservationStates] = usePersistedState(`${keyBase}__observationStates`, {});
  const [cannotUseFightingArts, setCannotUseFightingArts] = usePersistedState(`${keyBase}__cannotUseFightingArts`, false);

  const toggleObservation = (index, boxIndex) => {
    setObservationStates((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [boxIndex]: !prev[index]?.[boxIndex],
      },
    }));
  };

  const handleCategoryChange = (index, category) => {
    const newCategories = [...selectedCategories];
    newCategories[index] = category;
    setSelectedCategories(newCategories);

    setSelectedArts((prev) => {
      const updated = [...prev];
      updated[index] = null;
      return updated;
    });

    setObservationStates((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleArtChange = (index, art) => {
    const updatedArts = [...selectedArts];
    if (art === 'random' && selectedCategories[index]) {
      const available = categories[selectedCategories[index]] || [];
      if (available.length > 0) {
        updatedArts[index] = available[Math.floor(Math.random() * available.length)];
      }
    } else {
      const list = categories[selectedCategories[index]] || [];
      updatedArts[index] = list.find((item) => item.name === art) || null;
    }
    setSelectedArts(updatedArts);

    setObservationStates((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const [selectedPhilosophy, setSelectedPhilosophy] = usePersistedState(`${keyBase}__selectedPhilosophy`, '');
  const [rank, setRank] = usePersistedState(`${keyBase}__rank`, '1');

  const [selectedTenetLevel, setSelectedTenetLevel] = usePersistedState(`${keyBase}__selectedTenetLevel`, '');
  const [tenetObservationStates, setTenetObservationStates] = usePersistedState(`${keyBase}__tenetObservationStates`, {});

  const toggleTenetObservation = (boxIndex) => {
    setTenetObservationStates((prev) => ({
      ...prev,
      [boxIndex]: !prev?.[boxIndex],
    }));
  };

  const selectedPhilosophyDetails = selectedPhilosophy
    ? Philosophy.find((ph) => ph.name === selectedPhilosophy)
    : null;

  const tenetKnowledgeLevels = selectedPhilosophy === "Ambitionism"
    ? Knowledge.slice().sort((a, b) => safeName(a?.name).localeCompare(safeName(b?.name)))
    : selectedPhilosophyDetails
      ? Knowledge.filter((k) => k.name === selectedPhilosophyDetails.tenet)
      : [];

  const selectedTenetDetails = selectedTenetLevel
    ? tenetKnowledgeLevels.find((k) => `${k.name}${k.level ? ` - Level ${k.level}` : ''}` === selectedTenetLevel) || null
    : null;

  const selectedPhilosophyFlavor = selectedPhilosophyDetails ? selectedPhilosophyDetails.flavor : '';

  const handleTenetLevelChange = (event) => {
    const value = event.target.value;
    setSelectedTenetLevel(value);
    setTenetObservationStates({});
  };

  useEffect(() => {
    setSelectedTenetLevel('');
    setTenetObservationStates({});
  }, [selectedPhilosophy, setSelectedTenetLevel, setTenetObservationStates]);

  const [huntXPArray, setHuntXPArray] = useState([]);
  useEffect(() => {
    const selected = Philosophy.find((philo) => philo.name === selectedPhilosophy);
    setHuntXPArray(selected ? selected.huntxp : []);
  }, [selectedPhilosophy]);

  const [selectedKnowledges, setSelectedKnowledges] = usePersistedState(`${keyBase}__selectedKnowledges`, ['', '']);
  const [knowledgeObservationStates, setKnowledgeObservationStates] = usePersistedState(`${keyBase}__knowledgeObservationStates`, {});
  const [cannotUseKnowledge, setCannotUseKnowledge] = usePersistedState(`${keyBase}__cannotUseKnowledge`, false);

  const toggleKnowledgeObservation = (index, boxIndex) => {
    setKnowledgeObservationStates((prev) => ({
      ...prev,
      [index]: {
        ...prev[index],
        [boxIndex]: !prev[index]?.[boxIndex],
      },
    }));
  };

  const handleKnowledgeChange = (index, value) => {
    const updated = [...selectedKnowledges];
    updated[index] = value;
    setSelectedKnowledges(updated);

    setKnowledgeObservationStates((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const selectedKnowledgeDetails = selectedKnowledges.map((val) => {
    if (!val) return null;
    return Knowledge.find((item) => `${item.name}${item.level ? ` - Level ${item.level}` : ''}` === val) || null;
  });

  return (
    <>
      {showFightingArts && (
        <div className="fightingArts">
          <div className="fightingArts-header">
            <label>Fighting Arts: <span className="max-text">(Maximum 1 of Each)</span></label>
            <label><span className="vaultless">When you gain a fighting art, instead 📖 Vaultless</span></label>
          </div>

          {[0, 1].map((index) => (
            <div key={index} className="fightingart-dropdown-section">
              <select
                value={selectedCategories[index]}
                onChange={(e) => handleCategoryChange(index, e.target.value)}
                disabled={cannotUseFightingArts}
                className="fightingart-dropdown"
              >
                <option value="">Select Category</option>
                {index === 0 ? (
                  <>
                    <option value="Fighting Arts">Fighting Arts</option>
                    <option value="Strain Fighting Arts">Strain Fighting Arts</option>
                  </>
                ) : (
                  <option value="Secret Fighting Arts">Secret Fighting Arts</option>
                )}
              </select>

              {selectedCategories[index] && (
                <select
                  value={selectedArts[index]?.name || ''}
                  onChange={(e) => handleArtChange(index, e.target.value)}
                  disabled={cannotUseFightingArts}
                  className="fightingart-dropdown"
                >
                  <option value="">Select Fighting Art</option>
                  <option value="random">🎲 Random Selection</option>
                  {(categories[selectedCategories[index]] || []).map((art) => (
                    <option key={art.name} value={art.name}>{art.name}</option>
                  ))}
                </select>
              )}

              {selectedArts[index] && (
                <div className="art-details">
                  <strong>{selectedArts[index].name}</strong>
                  {selectedArts[index].definition && <p>{selectedArts[index].definition}</p>}
                  {selectedArts[index].rules && <p>{selectedArts[index].rules}</p>}

                  {selectedArts[index].observationCondition && (
                    <div className="observation-container">
                      <div className="observation-text">{selectedArts[index].observationCondition}</div>
                      <div className="observation-checkboxes">
                        {Array.from({ length: selectedArts[index].boxes || 0 }).map((_, boxIndex) => (
                          <label key={boxIndex} className="observation-checkbox-label">
                            <input
                              type="checkbox"
                              checked={observationStates[index]?.[boxIndex] || false}
                              onChange={() => toggleObservation(index, boxIndex)}
                              className="observation-checkbox"
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {selectedArts[index].levelUp && (
                    <div className="levelup-container">
                      <p><strong>Level Up:</strong> {selectedArts[index].levelUp}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showPhilosophy && (
        <div className="philosophy-section">
          <div className="huntXP-section">
            <label>Hunt XP:</label>
            <div className="huntXP-boxes">
              {Array.from({ length: 16 }, (_, index) => {
                const boxNumber = index + 1;
                const isBold = huntXPArray.includes(boxNumber);
                return (
                  <div key={boxNumber} className={`huntXP-box ${isBold ? 'bold' : ''}`}>
                    {boxNumber}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="philosophy-main-section">
            <div className="philosophy-flavor-rank">
              <div className="philosophy-container">
                <label>Philosophy:</label>
                <select
                  value={selectedPhilosophy}
                  onChange={(e) => setSelectedPhilosophy(e.target.value)}
                  className="philosophy-dropdown"
                >
                  <option value="">Select Philosophy</option>
                  {Philosophy.slice().sort((a, b) => safeName(a?.name).localeCompare(safeName(b?.name))).map((ph) => (
                    <option key={ph.name} value={ph.name}>{ph.name}</option>
                  ))}
                </select>
              </div>

              <div className="flavor-container">
                <span>{selectedPhilosophyFlavor}</span>
              </div>

              <div className="rank-container">
                <label>Rank:</label>
                <select value={rank} onChange={(e) => setRank(e.target.value)} className="rank-dropdown">
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="triangle">Triangle</option>
                </select>
              </div>
            </div>

            <div className="ponder-container">
              <p className="ponder">
                Ponder: If you are a returning survivor and reach a new Hunt XP milestone, you must rank up your philosophy.
                Limit, once per settlement phase.
              </p>
            </div>

            {selectedPhilosophyDetails?.neurosis && (
              <div className="neurosis-section">
                <strong>Neurosis: {selectedPhilosophyDetails.neurosis}</strong>
                <p>{selectedPhilosophyDetails.neurosisDefinition}</p>
              </div>
            )}

            {selectedPhilosophyDetails && (
              <div className="knowledge-dropdown-section">
                <label>Tenet Knowledge: </label>
                <select
                  value={selectedTenetLevel}
                  onChange={handleTenetLevelChange}
                  className="knowledge-dropdown"
                >
                  <option value="">Select Knowledge Level</option>
                  {tenetKnowledgeLevels.map((level, index) => (
                    <option key={index} value={`${level.name}${level.level ? ` - Level ${level.level}` : ''}`}>
                      {level.name} {level.level ? `- Level ${level.level}` : ''}
                    </option>
                  ))}
                </select>

                {selectedTenetDetails && (
                  <div className="knowledge-details">
                    <p><strong>Rules:</strong> {selectedTenetDetails.rules || 'N/A'}</p>

                    {selectedTenetDetails.observationCondition && (
                      <div className="observation-section">
                        <p><strong>Observation:</strong> {selectedTenetDetails.observationCondition}</p>
                        <div className="observation-checkboxes">
                          {Array.from({ length: selectedTenetDetails.boxes || 0 }).map((_, boxIndex) => (
                            <label key={boxIndex}>
                              <input
                                type="checkbox"
                                checked={tenetObservationStates[boxIndex] || false}
                                onChange={() => toggleTenetObservation(boxIndex)}
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {showKnowledge && (
        <div className="knowledges">
          <div className="knowledge-header">
            <label>Knowledges:</label>
            <div className="knowledge-condition">
              <input
                type="checkbox"
                checked={cannotUseKnowledge}
                onChange={() => setCannotUseKnowledge(!cannotUseKnowledge)}
              />
              <span role="img" aria-label="lock">🔒</span> If you cannot use fighting arts, you cannot use Knowledge.
            </div>
          </div>

          {[0, 1].map((index) => (
            <div key={index} className="knowledge-dropdown-section">
              <label>Select Knowledge:</label>
              <select
                value={selectedKnowledges[index] || ''}
                onChange={(e) => handleKnowledgeChange(index, e.target.value)}
                className="knowledge-dropdown"
                disabled={cannotUseKnowledge}
              >
                <option value="">Select Knowledge</option>
                {Knowledge.map((knowledge) => (
                  <option
                    key={`${knowledge.name}-${knowledge.level || ''}`}
                    value={`${knowledge.name}${knowledge.level ? ` - Level ${knowledge.level}` : ''}`}
                  >
                    {knowledge.name} {knowledge.level ? `- Level ${knowledge.level}` : ''}
                  </option>
                ))}
              </select>

              {selectedKnowledgeDetails[index] && (
                <div className="knowledge-details">
                  <p><strong>Rules:</strong> {selectedKnowledgeDetails[index].rules || 'N/A'}</p>

                  {selectedKnowledgeDetails[index].observationCondition && (
                    <div className="observation-section">
                      <p><strong>Observation:</strong> {selectedKnowledgeDetails[index].observationCondition}</p>
                      <div className="observation-checkboxes">
                        {Array.from({ length: selectedKnowledgeDetails[index].boxes || 0 }).map((_, boxIndex) => (
                          <label key={boxIndex}>
                            <input
                              type="checkbox"
                              checked={knowledgeObservationStates[index]?.[boxIndex] || false}
                              onChange={() => toggleKnowledgeObservation(index, boxIndex)}
                            />
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default ArcCharacterSheets;
